/**
 * /cart/<token> — abandoned-cart recovery landing.
 *
 * Server flow (createServerFn wrapped for the client hydration effect):
 *   1. Validate token against the mint alphabet locally.
 *   2. Atomically consume via `consume_cart_recovery_v2` — a SECURITY
 *      DEFINER RPC that locks the token + engagement_cart_snapshot row
 *      with FOR UPDATE SKIP LOCKED and validates every gate in-DB:
 *      token expiry/revocation/single-use, snapshot state=active,
 *      snapshot revision match, snapshot expires_at/retention_ends_at,
 *      canonical item schema, and recipient binding.
 *   3. The RPC returns ONLY canonical restore fields — no snapshot_id,
 *      no lead_profile_id, no customer_id ever reach the browser.
 *   4. We rehydrate product titles / images / current price from the
 *      trusted `products`+`variants` catalog server-side. Token JSON is
 *      NEVER trusted for prices or titles.
 *   5. Any failure — invalid token, expired snapshot, mismatched items,
 *      catalog miss — maps to the same generic "link expired" screen.
 *      No IDs, no phone, no error detail, no timing side-channel.
 *   6. Same-origin navigation only; noindex/nofollow via head meta.
 *   7. The raw token appears only in the URL bar. It is never logged,
 *      never sent to analytics, never printed by this module.
 *
 * There is NO reference to the `reservations` table in this file — cart
 * recovery is fully served by engagement_cart_snapshots + v2 RPC.
 */
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useCartStore, type CartItem } from "@/stores/cartStore";

type RestoredLine = {
  variantId: string;
  productHandle: string;
  productTitle: string;
  imageUrl: string | null;
  priceInr: number;
  quantity: number;
};

type RecoveryOffer = {
  code: string;
  discountPercent: number;
  prepaidOnly: boolean;
  expiresAt: string;
  discountType: "percent" | "flat";
  maxDiscountAmount: number | null;
};

type RestoreResult =
  | { ok: true; items: RestoredLine[]; offer: RecoveryOffer | null }
  | { ok: false };

const inputSchema = z.object({ token: z.string().min(16).max(64) });

const restoreCart = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => inputSchema.parse(raw))
  .handler(async ({ data }): Promise<RestoreResult> => {
    // Dynamic imports keep server-only modules out of the client bundle.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { hashForStorage, _internals } = await import(
      "@/lib/engagement/cart-recovery.server"
    );

    // Validate the token locally against the mint alphabet BEFORE hashing.
    // A malformed token never touches the RPC (no timing signal either
    // way — both paths return the same generic outcome).
    if (!_internals.TOKEN_RE.test(data.token)) return { ok: false };
    const _token_hash = hashForStorage(data.token);

    // Consume atomically. RPC returns [] on any invalid gate.
    const { data: rpcData, error: rpcErr } = await supabaseAdmin.rpc(
      "consume_cart_recovery_v2",
      { _token_hash },
    );
    if (rpcErr) return { ok: false };
    const row = Array.isArray(rpcData) ? rpcData[0] : null;
    if (!row) return { ok: false };

    // Canonical restore fields ONLY — no internal IDs.
    const rawItems = Array.isArray(row.items) ? (row.items as unknown[]) : [];
    if (rawItems.length === 0) return { ok: false };

    // Validate canonical item shape at the seam. The RPC already runs
    // engagement_cart_items_valid, but re-checking here means a schema
    // drift can never sneak untrusted keys past this module.
    type CanonRow = { handle: string; variant_id: string; qty: number };
    const canonical: CanonRow[] = [];
    for (const it of rawItems) {
      if (!it || typeof it !== "object") return { ok: false };
      const o = it as Record<string, unknown>;
      const keys = Object.keys(o).sort();
      if (
        keys.length !== 3 ||
        keys[0] !== "handle" ||
        keys[1] !== "qty" ||
        keys[2] !== "variant_id"
      ) {
        return { ok: false };
      }
      const handle = typeof o.handle === "string" ? o.handle : "";
      const variant_id = typeof o.variant_id === "string" ? o.variant_id : "";
      const qty = typeof o.qty === "number" ? o.qty : NaN;
      if (!handle || !variant_id) return { ok: false };
      if (!Number.isInteger(qty) || qty < 1 || qty > 20) return { ok: false };
      canonical.push({ handle, variant_id, qty });
    }

    // Rehydrate titles / images / CURRENT price server-side from the
    // trusted catalog — token JSON is not trusted for prices or titles.
    const uuidKeys = Array.from(new Set(canonical.map((c) => c.variant_id)));
    const { data: variantRows, error: vErr } = await supabaseAdmin
      .from("variants")
      .select("id,shopify_id,price_paise,active,product_id,title")
      .in("id", uuidKeys);
    if (vErr || !variantRows) return { ok: false };

    const productIds = Array.from(new Set(variantRows.map((v) => v.product_id)));
    const { data: productRows, error: pErr } = await supabaseAdmin
      .from("products")
      .select("id,handle,title,image_url")
      .in("id", productIds);
    if (pErr || !productRows) return { ok: false };
    const productById = new Map(productRows.map((p) => [p.id, p]));
    const variantById = new Map(variantRows.map((v) => [v.id, v]));

    const items: RestoredLine[] = [];
    for (const c of canonical) {
      const v = variantById.get(c.variant_id);
      if (!v || !v.active) return { ok: false };
      const p = productById.get(v.product_id);
      if (!p || p.handle !== c.handle) return { ok: false };
      const priceInr = Math.round((v.price_paise ?? 0) / 100);
      items.push({
        variantId: v.shopify_id ?? v.id,
        productHandle: p.handle,
        productTitle: (p.title ?? p.handle).slice(0, 200),
        imageUrl: (p.image_url as string | null) ?? null,
        priceInr,
        quantity: c.qty,
      });
    }
    if (items.length === 0) return { ok: false };

    // Best eligible public offer, validated through the same lookup the
    // checkout uses. Never block cart restore on coupon errors.
    let offer: RecoveryOffer | null = null;
    try {
      const { lookupCoupon } = await import("@/lib/coupons.server");
      const subtotalPaise = items.reduce((n, i) => n + i.priceInr * 100 * i.quantity, 0);
      const check = await lookupCoupon(supabaseAdmin, { code: "WELCOME10", subtotalPaise, prepaid: false });
      if (check.ok) {
        offer = {
          code: check.code,
          discountPercent: check.discountPercent,
          prepaidOnly: check.prepaidOnly,
          expiresAt: check.expiresAt,
          discountType: check.discountType,
          maxDiscountAmount: check.maxDiscountPaise != null ? check.maxDiscountPaise / 100 : null,
        };
      }
    } catch {
      offer = null;
    }
    return { ok: true, items, offer };
  });

export const Route = createFileRoute("/cart/$token")({
  ssr: false, // client-only hydration; server work happens via the fn call
  component: CartRecoveryPage,
  head: () => ({
    meta: [
      { title: "Restoring your cart · TruWe" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  notFoundComponent: () => <ExpiredView />,
  errorComponent: () => <ExpiredView />,
});

function CartRecoveryPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const addItems = useCartStore((s) => s.addItems);
  const clearCart = useCartStore((s) => s.clearCart);
  const [status, setStatus] = useState<"loading" | "expired" | "restored">("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Deliberately do NOT log the token or any part of the response.
      const res = await restoreCart({ data: { token } });
      if (cancelled) return;
      if (!res.ok) {
        setStatus("expired");
        return;
      }
      clearCart();
      const items: Omit<CartItem, "lineId">[] = res.items.map((it) => ({
        variantId: it.variantId,
        productHandle: it.productHandle,
        productTitle: it.productTitle,
        imageUrl: it.imageUrl,
        price: { amount: String(it.priceInr), currencyCode: "INR" },
        quantity: it.quantity,
      }));
      await addItems(items);
      // Apply the best eligible public offer, validated server-side. Checkout
      // re-checks first-order eligibility against the real phone and drops
      // it for repeat buyers; a failed lookup simply restores without coupon.
      if (res.offer) {
        useCartStore.getState().setCoupon(res.offer);
      }
      setStatus("restored");
      // Same-origin only; the /shop path is validated statically at the
      // TanStack route boundary.
      setTimeout(() => navigate({ to: "/shop", replace: true }), 900);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, addItems, clearCart, navigate]);

  if (status === "expired") return <ExpiredView />;
  if (status === "restored") return <RestoredView />;
  return (
    <main className="min-h-[60vh] flex items-center justify-center px-6 text-center">
      <div>
        <h1 className="text-2xl font-medium">Restoring your cart…</h1>
        <p className="mt-2 text-sm opacity-70">One moment.</p>
      </div>
    </main>
  );
}

function ExpiredView() {
  return (
    <main className="min-h-[60vh] flex items-center justify-center px-6 text-center">
      <div>
        <h1 className="text-2xl font-medium">This link has expired</h1>
        <p className="mt-2 text-sm opacity-70">
          Recovery links are single-use and time-limited.
        </p>
        <Link
          to="/shop"
          className="inline-block mt-6 px-4 py-2 rounded-full bg-[color:var(--brand,#1E5EFF)] text-white text-sm"
        >
          Continue shopping
        </Link>
      </div>
    </main>
  );
}

function RestoredView() {
  return (
    <main className="min-h-[60vh] flex items-center justify-center px-6 text-center">
      <div>
        <h1 className="text-2xl font-medium">Cart restored</h1>
        <p className="mt-2 text-sm opacity-70">Taking you to shop…</p>
      </div>
    </main>
  );
}
